import React from 'react';
import {
  Box,
  Container,
  Grid,
  IconButton,
  Typography,
  Link,
  Button,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LockIcon from '@mui/icons-material/Lock';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useGlobalState } from '../../global/context/global-context';
import { getEntityLogoSrc, getEntityPowerLabel, getFormattedEntityName } from '../../../utils/entity-formatter';
import { useParams } from 'react-router-dom';
import { useNavigate } from '../../../hooks/useNavigate';
import { ISocialMediaReponse } from '../../../model/base/response/social-media-response';
import { RADIUS } from '../../../theme/tokens';

// Perfil cadastrado como URL completa ou só o usuário (com ou sem @)
const profileUrl = (base: string, profile: string) =>
  /^https?:\/\//i.test(profile) ? profile : `${base}/${profile.replace(/^@/, '')}`;

const whatsAppUrl = (number: string) => {
  const digits = number.replace(/\D/g, '');
  return `https://wa.me/${digits.length <= 11 ? `55${digits}` : digits}`;
};

const socialLinks = (social: ISocialMediaReponse | undefined) =>
  [
    social?.facebookProfile && { label: 'Facebook', href: profileUrl('https://facebook.com', social.facebookProfile), icon: <FacebookIcon /> },
    social?.instagramProfile && { label: 'Instagram', href: profileUrl('https://instagram.com', social.instagramProfile), icon: <InstagramIcon /> },
    social?.twitterProfile && { label: 'X (Twitter)', href: profileUrl('https://x.com', social.twitterProfile), icon: <TwitterIcon /> },
    social?.whatsAppNumber && { label: 'WhatsApp', href: whatsAppUrl(social.whatsAppNumber), icon: <WhatsAppIcon /> },
  ].filter(Boolean) as { label: string; href: string; icon: React.ReactElement }[];

// Texto sobre brand.deep: branco com opacidade que mantém contraste AA
const mutedText = (theme: any) => alpha(theme.palette.common.white, 0.78);

const footerHeadingSx = {
  fontFamily: (theme: any) => theme.typography.h1.fontFamily,
  fontSize: '1rem',
  fontWeight: 600,
  color: 'common.white',
  mb: 2,
} as const;

const footerLinkSx = {
  color: mutedText,
  fontSize: '0.9375rem',
  textDecoration: 'none',
  cursor: 'pointer',
  width: 'fit-content',
  '&:hover': { color: 'common.white', textDecoration: 'underline' },
} as const;

export default function AppFooter() {
  const { applicationData, parametersPortal } = useGlobalState();
  const navigate = useNavigate();
  const params = useParams<{ state?: string; city?: string }>();
  const entity = applicationData?.entity;
  const cnpj = entity?.document || '';
  const operatingHours = parametersPortal?.operatingHours || 'Segunda a Sexta: 8h às 17h';
  const logo = getEntityLogoSrc(entity);
  const social = socialLinks(entity?.socialMedia);
  const address = entity?.address;

  const isSecure = window.location.protocol === 'https:' || window.location.hostname === 'localhost';

  const getHref = (path: string) => {
    if (params.state && params.city) {
      return `/${params.state}/${params.city}${path}`;
    }
    return path;
  };

  const quickLinks = [
    { label: 'Início', href: '/', external: false },
    { label: 'A Cidade', href: '/a-cidade', external: false },
    { label: 'Notícias', href: '/noticias', external: false },
    { label: 'Diário Oficial', href: '/diario-oficial', external: false },
    { label: 'Licitações', href: '/licitacoes', external: false },
    { label: 'Contas Públicas', href: '/contas-publicas', external: false },
    { label: 'Contato', href: '/contato', external: false },
  ];

  const contactRow = (icon: React.ReactElement, content: React.ReactNode) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
      <Box sx={{ display: 'inline-flex', color: 'common.white', opacity: 0.85, mt: '2px', '& .MuiSvgIcon-root': { fontSize: '1.125rem' } }}>
        {icon}
      </Box>
      <Typography variant="body2" sx={{ color: mutedText }}>
        {content}
      </Typography>
    </Box>
  );

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: 'brand.deep',
        color: 'common.white',
        pt: { xs: 6, md: 8 },
        pb: 3,
      }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={{ xs: 5, md: 6 }}>
          {/* Identificação da entidade */}
          <Grid item xs={12} md={5}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
              {logo && (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: `${RADIUS.card}px`,
                    backgroundColor: 'common.white',
                    display: 'inline-flex',
                    flexShrink: 0,
                  }}
                >
                  <Box component="img" src={logo} alt="" sx={{ height: 52, width: 'auto', maxWidth: 96, objectFit: 'contain' }} />
                </Box>
              )}
              <Box>
                <Typography
                  component="p"
                  sx={{ fontFamily: (theme) => theme.typography.h1.fontFamily, fontWeight: 600, fontSize: '1.125rem', lineHeight: 1.3 }}
                >
                  {getFormattedEntityName(entity)}
                </Typography>
                <Typography variant="overline" component="p" sx={{ color: mutedText }}>
                  {getEntityPowerLabel(entity)}
                </Typography>
              </Box>
            </Box>

            {entity?.formmatedDocument && (
              <Typography variant="body2" sx={{ color: mutedText, fontVariantNumeric: 'tabular-nums' }}>
                CNPJ: {entity.formmatedDocument}
              </Typography>
            )}

            <Box sx={{ mt: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {[
                { label: 'e-OUV', href: `http://docgedsistemas.com.br/ouvidoria?codEntidade=${cnpj}&empresa=3851` },
                { label: 'e-SIC', href: `http://docgedsistemas.com.br/sic?codEntidade=${cnpj}&empresa=3851` },
              ].map((link) => (
                <Button
                  key={link.label}
                  variant="outlined"
                  size="small"
                  component="a"
                  href={link.href}
                  target="_blank"
                  rel="noopener"
                  sx={{
                    color: 'common.white',
                    borderColor: (theme) => alpha(theme.palette.common.white, 0.5),
                    '&:hover': {
                      borderColor: 'common.white',
                      backgroundColor: (theme) => alpha(theme.palette.common.white, 0.1),
                    },
                  }}
                >
                  {link.label}
                </Button>
              ))}
            </Box>

            {social.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography component="h2" sx={{ ...footerHeadingSx, mb: 1 }}>
                  Redes sociais
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, ml: -1 }}>
                  {social.map((link) => (
                    <Tooltip key={link.label} title={link.label} arrow>
                      <IconButton
                        component="a"
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={link.label}
                        sx={{
                          width: 44,
                          height: 44,
                          color: 'common.white',
                          '&:hover': { backgroundColor: (theme) => alpha(theme.palette.common.white, 0.12) },
                        }}
                      >
                        {link.icon}
                      </IconButton>
                    </Tooltip>
                  ))}
                </Box>
              </Box>
            )}
          </Grid>

          {/* Navegação */}
          <Grid item xs={12} sm={5} md={3}>
            <Typography component="h2" sx={footerHeadingSx}>
              Navegar pelo site
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={getHref(link.href)}
                    onClick={(e) => {
                      if (!link.external) {
                        e.preventDefault();
                        navigate(link.href);
                      }
                    }}
                    sx={footerLinkSx}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </Box>
          </Grid>

          {/* Contato */}
          <Grid item xs={12} sm={7} md={4}>
            <Typography component="h2" sx={footerHeadingSx}>
              {entity?.entityType === 1 ? 'Gabinete do Prefeito' : 'Atendimento'}
            </Typography>
            {entity?.entityType === 1 && (
              <Typography variant="body2" sx={{ color: mutedText, mb: 2.5 }}>
                Órgão de assessoramento direto, que auxilia o processo decisório do Chefe do Poder
                Executivo.
              </Typography>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {entity?.contact?.formattedBusinessPhone && contactRow(
                <PhoneIcon />,
                <>
                  {entity.contact.formattedBusinessPhone}
                  {entity.contact.formattedMessagePhone && ` / ${entity.contact.formattedMessagePhone}`}
                </>
              )}

              {entity?.contact?.email && contactRow(
                <EmailIcon />,
                <Box component="a" href={`mailto:${entity.contact.email}`} sx={{ ...footerLinkSx, fontSize: 'inherit', wordBreak: 'break-word' }}>
                  {entity.contact.email}
                </Box>
              )}

              {address && contactRow(
                <LocationOnIcon />,
                <>
                  {address.street}, {address.number}, {address.neighborhood}, {address.city} - {address.state},{' '}
                  {address.formattedZipCode}
                </>
              )}

              {contactRow(<AccessTimeIcon />, operatingHours)}
            </Box>
          </Grid>
        </Grid>

        {/* Linha final */}
        <Box
          sx={{
            mt: { xs: 5, md: 7 },
            pt: 3,
            borderTop: 1,
            borderColor: (theme) => alpha(theme.palette.common.white, 0.16),
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            gap: 3,
          }}
        >
          <Box>
            <Typography variant="body2" sx={{ color: mutedText }}>
              © {new Date().getFullYear()} {getFormattedEntityName(entity)}. Todos os direitos reservados.
            </Typography>
            <Typography variant="caption" sx={{ color: mutedText, display: 'block', mt: 0.5 }}>
              Desenvolvido por DocGed Serviços ·{' '}
              <Link
                href={getHref('/acessibilidade')}
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/acessibilidade');
                }}
                sx={{ ...footerLinkSx, fontSize: 'inherit' }}
              >
                Acessibilidade
              </Link>
            </Typography>
          </Box>

          {/* Selos: fundo branco para manter as cores originais das imagens */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Link
              href={`https://validator.w3.org/nu/?doc=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: 'flex', borderRadius: 1, overflow: 'hidden', '&:hover': { opacity: 0.85 } }}
            >
              <Box
                component="img"
                src="https://www.w3.org/Icons/valid-html401"
                alt="Valid HTML"
                sx={{ height: 31, width: 88 }}
              />
            </Link>

            {isSecure && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.5,
                  height: 36,
                  borderRadius: 1,
                  border: 1,
                  borderColor: (theme) => alpha(theme.palette.common.white, 0.4),
                }}
              >
                <LockIcon sx={{ fontSize: '1rem' }} />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Site seguro
                </Typography>
              </Box>
            )}

            <Link
              href="https://radardatransparencia.atricon.org.br/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: 'flex', p: 0.5, borderRadius: 1, backgroundColor: 'common.white', '&:hover': { opacity: 0.85 } }}
            >
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/assets/images/logo_radar_transparencia.jpg`}
                alt="Radar da Transparência"
                sx={{ height: 32, width: 'auto' }}
              />
            </Link>

            <Link
              href={getHref('/acessibilidade')}
              onClick={(e) => {
                e.preventDefault();
                navigate('/acessibilidade');
              }}
              sx={{ display: 'flex', p: 0.5, borderRadius: 1, backgroundColor: 'common.white', '&:hover': { opacity: 0.85 } }}
            >
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/assets/images/logo_acessibilidade.png`}
                alt="Acessibilidade"
                sx={{ height: 32, width: 'auto' }}
              />
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
